import React, { useEffect, useState } from "react";

import axios from "axios";
import toast from "react-hot-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { getCurrencies } from "../../../utils/get-currencies";
import { formatOptions } from "../../../utils/helpers";
import Select from "react-select";
import { useRequestStore } from "../../../data/stores/loggerStore";
const formSchema = z.object({
  erpModule: z.string().min(1, "Data system is required"),
  systemDeveloper: z.string().min(1, "System developer name is required"),
  dateInstalled: z.string().min(1, "Installation date is required"),
  annualLicenseFee: z.string().min(1, "Annual license fees are required"),
  systemPurpose: z.string().min(1, "Main purpose description is required"),
  annualLicenseFeeCurrency: z.string().min(1, "Currency is Required"),
});

type FormData = z.infer<typeof formSchema>;

const AboutEdit = ({ data, setIsEditing }) => {
  const [currencies, setCurrencies] = useState<string[]>([]);
  const { dataSystemAll, getDynamicLogger }: any = useRequestStore();
  useEffect(() => {
    (async () => {
      const list = await getCurrencies();
      setCurrencies(list);
    })();
  }, []);

  function formatDateForInput(dateString?: string) {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toISOString().split("T")[0]; // "YYYY-MM-DD"
  }
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      // informationCollectionTool: data?.informationCollectionTool || undefined,
      erpModule: data?.erpModule || undefined,
      systemDeveloper: data?.systemDeveloper || undefined,
      dateInstalled: data?.dateInstalled || undefined,
      annualLicenseFee: data?.annualLicenseFee || undefined,
      systemPurpose: data?.systemPurpose || undefined,
      annualLicenseFeeCurrency: data?.annualLicenseFeeCurrency || undefined,
    },
  });

  // const techOptions = formatOptions(informationCollectionToolList);
  const currencyList = ([] = formatOptions(currencies));

  const onSubmit = async (itemForm: any) => {
    try {
      const res = await axios.put(`/api/v1/data-system`, {
        // _id: data?._id,
        datasystems: [{ ...itemForm, _id: data?._id }],
        email: data.profile.user.email,
        user: data.profile?.user?._id,
      });
      const updatedArray = res?.data?.data;

      const newDocs = dataSystemAll.docs.map((d: any) => {
        const updated = updatedArray.find((u: any) => u._id === d._id);
        if (!updated) return d;
        return {
          ...d,
          ...updated,
          profile:
            typeof updated.profile === "string" ? d.profile : updated.profile,
        };
      });
      const updatedDataSystemAll = { ...dataSystemAll, docs: newDocs };
      getDynamicLogger(updatedDataSystemAll, "dataSystemAll");
      toast.success(
        res?.data?.message ||
          "Data system updated, Changes would appear shortly."
      );
      // toast.success("Data system updated successfully.");
      setIsEditing(false);
    } catch (error: any) {
      toast.error(error?.response?.data?.message || "Something went wrong");
    }
  };
  const handleCancel = () => {
    reset({
      // informationCollectionTool: data?.informationCollectionTool || undefined,
      erpModule: data?.erpModule || undefined,
      systemDeveloper: data?.systemDeveloper || undefined,
      dateInstalled: data?.dateInstalled || undefined,
      annualLicenseFee: data?.annualLicenseFee || undefined,
      systemPurpose: data?.systemPurpose || undefined,
      annualLicenseFeeCurrency: data?.annualLicenseFeeCurrency || undefined,
    });
    setIsEditing(false);
  };
  return (
    <div className="font-Inter">
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Data system in use by the department{" "}
            <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            {...register("erpModule")}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.erpModule ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="ERP Module 1"
          />
          {errors.erpModule && (
            <p className="text-red-500 text-xs mt-1">
              {errors.erpModule.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Name of the system developer <span className="text-red-500">*</span>
          </label>
          <input
            {...register("systemDeveloper")}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.systemDeveloper ? "border-red-500" : "border-gray-300"
            }`}
          />
          {errors.systemDeveloper && (
            <p className="text-red-500 text-xs mt-1">
              {errors.systemDeveloper.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Date of first installation <span className="text-red-500">*</span>
          </label>
          <input
            type="date"
            defaultValue={formatDateForInput(data?.dateInstalled)}
            {...register("dateInstalled")}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.dateInstalled ? "border-red-500" : "border-gray-300"
            }`}
          />

          {errors.dateInstalled && (
            <p className="text-red-500 text-xs mt-1">
              {errors.dateInstalled.message}
            </p>
          )}
        </div>

        <div className="flex items-center gap-4 ">
          <div className="">
            <label className="block text-sm font-medium text-gray-700">
              Currency
              <span className="text-red-500">*</span>
            </label>
            <Controller
              name="annualLicenseFeeCurrency"
              control={control}
              rules={{ required: "Please select a currency" }}
              render={({ field }) => (
                <Select
                  options={currencyList} // already in { value, label } format
                  placeholder="Select Currency..."
                  onChange={(selected) => field.onChange(selected?.value)}
                  value={
                    currencyList.find((opt) => opt.value === field.value) ||
                    null
                  }
                />
              )}
            />
            {errors.annualLicenseFee && (
              <p className="text-red-500 text-xs mt-1">
                {errors.annualLicenseFee.message}
              </p>
            )}
          </div>
          <div className="">
            <label className="block text-sm font-medium text-gray-700">
              Amount paid for annual license fees or maintenance{" "}
              <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              {...register("annualLicenseFee")}
              className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                errors.annualLicenseFee ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter Annual License Fees"
            />
            {errors.annualLicenseFee && (
              <p className="text-red-500 text-xs mt-1">
                {errors.annualLicenseFee.message}
              </p>
            )}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Describe the main purpose of the system{" "}
            <span className="text-red-500">*</span>
          </label>
          <textarea
            {...register("systemPurpose")}
            rows={3}
            className={`w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              errors.systemPurpose ? "border-red-500" : "border-gray-300"
            }`}
            placeholder="e.g. Billing, NRW management, Payroll"
          />
          {errors.systemPurpose && (
            <p className="text-red-500 text-xs mt-1">
              {errors.systemPurpose.message}
            </p>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 pt-6">
          <button
            type="button"
            onClick={handleCancel}
            className="px-12 py-2.5 border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
          >
            Go Back
          </button>
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-12 py-2.5 bg-[#2070B8] font-medium text-white rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AboutEdit;
